import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import firebase from 'firebase/app';
import {
  Message,
  CollectionName,
  MessageType,
  BotAgentMessage,
} from '@tomis-tech/types';

import {
  useSession,
  ViewControllerContext,
  ViewController,
  useParent,
  TypingContext,
} from '../context';
import {
  View,
  IframePosition,
  getActualSize,
  getIframePosition,
} from '../view';
import { getTheme } from '../theme';
import { NumberPair, FirebaseMessage } from '../types';
import ChatButton from './ChatButton';
import ChatWindow from './ChatWindow';
import LatestMessagePreview from './LatestMessagePreview';
import { logError, Category } from '.././sentry-iframe';
import { GA4Event } from '../../tracking/event-types';
import { trackGA4Event } from '../../tracking';
import { FIREBASE, getFirebaseUserId } from '../../firebase';
import { SITE_SLUG } from '../consts';

/** Renders the entire chat widget (open and closed),
 * after the chatbot has been properly initialized */
const Chat = () => {
  const [isTyping, _setIsTyping] = useState(false);
  const timeoutRef = useRef(null);
  const { iframeResizeAMP, isMobile } = useParent();
  const viewController = useView();
  const { closedSpace } = getTheme(isMobile).widget;
  const isOpen = viewController.currentView === View.ChatWindowOpen;
  const { conversation, site } = useSession();
  const { botConfig } = site;
  const { messages, unseenCount, latestIncomingMessage } = useMessages(
    conversation.ref.id,
    isOpen
  );

  const hasLatestIncomingMessageBeenSeen = isMessageSeen(latestIncomingMessage);
  const latestMessageIsIncoming = isMessageFromSupportOrBot(messages[0]);

  /** Always show unseen badge */
  const showBadge = true;

  /** Show preview for new messages, except...
   *
   * - `botConfig.hidePreview` = true
   */
  const showPreview =
    !isOpen &&
    Boolean(latestIncomingMessage) &&
    !hasLatestIncomingMessageBeenSeen &&
    !botConfig.hidePreview;

  // GA4 event on initial load
  useEffect(() => {
    trackGA4Event(GA4Event.Loaded);
  }, []);

  /** Set iframe size when chat widget is closed */
  useEffect(() => {
    if (!isOpen) {
      if (showPreview) {
        viewController.transitionToView(View.ClosedWithPreview);
      } else {
        viewController.transitionToView(View.ClosedWithoutPreview);
      }
    }
  }, [isOpen, showPreview]);

  // guarantee that typing isn't showing when it shouldn't be
  useEffect(() => {
    if (latestMessageIsIncoming && isTyping) {
      setIsTyping(false);
    }
  }, [latestMessageIsIncoming, isTyping]);

  /** Open Chat Widget */
  function open() {
    if (typeof iframeResizeAMP === 'function') {
      iframeResizeAMP(600);
    }
    viewController.transitionToView(View.ChatWindowOpen);
    trackGA4Event(GA4Event.WindowOpened, {}, true);
  }

  /** Close Chat Widget  */
  function close() {
    if (typeof iframeResizeAMP === 'function') {
      iframeResizeAMP(101);
    }
    viewController.transitionToView(View.ClosedWithoutPreview);
    trackGA4Event(GA4Event.WindowClosed, {}, true);
  }

  const viewControllerContextValue = {
    ...viewController,
    open,
    close,
  };

  /** Make ChatBot appear to be typing/thinking after 500ms delay */
  function setIsTyping(newIsTyping: boolean) {
    clearTimeout(timeoutRef.current);
    if (newIsTyping) {
      const id = setTimeout(() => {
        _setIsTyping(true);
      }, 500);
      timeoutRef.current = id;
    } else {
      _setIsTyping(false);
    }
  }

  const typingContextValue = { isTyping, setIsTyping };

  return (
    <ViewControllerContext.Provider value={viewControllerContextValue}>
      <div
        css={(theme: Theme) => ({
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          margin: isMobile && isOpen ? 0 : theme.widget.padding,
          perspective: 900,
          transform: `translate(${isOpen ? '0' : closedSpace}px, ${
            isOpen ? '0' : closedSpace
          }px)`,
        })}
      >
        <TypingContext.Provider value={typingContextValue}>
          <ChatButton
            isOpen={isOpen}
            onClick={open}
            unseenCount={showBadge ? unseenCount : 0}
          />
          <LatestMessagePreview
            onClick={open}
            showPreview={showPreview}
            latestImportantMessage={latestIncomingMessage}
            settlePosition={viewController.settlePosition}
          />
          <ChatWindow
            isOpen={isOpen}
            messages={messages}
            settlePosition={viewController.settlePosition}
          />
        </TypingContext.Provider>
      </div>
    </ViewControllerContext.Provider>
  );
};
export default Chat;

/*************************************************************************************/

const useView = (): Partial<ViewController> => {
  const {
    setPosition,
    isMobile,
    parentWindowSize: [parentWidth, parentHeight],
  } = useParent();
  const { site } = useSession();
  const { styleOptions } = site.botConfig;

  const {
    widget: { closedSpace },
  } = getTheme(isMobile);
  const [viewportOffset, setViewportOffset] = useState<NumberPair>([
    closedSpace,
    closedSpace,
  ]);

  const [currentView, setCurrentView] = useState<View>(
    View.ClosedWithoutPreview
  );

  const currentPosition = useMemo(
    () => getIframePosition(currentView, isMobile, styleOptions),
    [currentView, isMobile]
  );

  const transitionToView = useCallback(
    (view: View) => {
      const targetPosition = getIframePosition(view, isMobile, styleOptions);

      const maxPosition: IframePosition = [
        [
          Math.max(
            getActualSize(currentPosition[0][0], parentWidth),
            getActualSize(targetPosition[0][0], parentWidth)
          ),
          Math.max(
            getActualSize(currentPosition[0][1], parentHeight),
            getActualSize(targetPosition[0][1], parentHeight)
          ),
        ],
        [
          Math.min(currentPosition[1][0], targetPosition[1][0]),
          Math.min(currentPosition[1][1], targetPosition[1][1]),
        ],
      ];

      // const t0 = performance.now();
      setPosition(maxPosition, view).then(() => {
        setViewportOffset(maxPosition[1]);
        setCurrentView(view);
        // const t1 = performance.now();
      });
    },
    [isMobile, currentPosition, parentWidth, parentHeight]
  );

  // This is to be called when animation completes
  const settlePosition = useCallback(() => {
    setPosition(currentPosition).then(() => {
      setViewportOffset(currentPosition[1]);
    });
  }, [currentPosition]);

  return useMemo(
    () => ({
      currentView,
      transitionToView,
      settlePosition,
      viewportOffset,
    }),
    [currentView, transitionToView, settlePosition, viewportOffset]
  );
};

/** Has Message been seen by Firebase User (aka person talking to bot) */
const isMessageSeen = (message?: Message) => {
  const firebaseUserId = getFirebaseUserId();

  return (
    message &&
    Array.isArray(message.seenBy) &&
    message.seenBy.indexOf(firebaseUserId) > -1
  );
};

/** Is Message from a Live Chat support agent or the ChatBot? i.e. incoming message! */
const isMessageFromSupportOrBot = (message?: Message): boolean => {
  if (!message) return false;
  return (
    (message.type === MessageType.User && message.userType !== 'customer') ||
    message.type === MessageType.BotAgent
  );
};

/** Get all messages from Firebase */
const useMessages = (
  conversationId: string,
  isOpen: boolean
): {
  /** All messages to display in Chat. Includes all messages except "UserActions"
   * such as Page Views */
  messages: Message[];
  /** Unseen message count for Live Chat or ChatBot messages */
  unseenCount: number;
  /** Most recent incoming message from Live Chat or ChatBot */
  latestIncomingMessage: Message | undefined;
} => {
  const [messages, setMessages] = useState<Message[]>([]);

  const firebaseUserId = getFirebaseUserId();

  useEffect(() => {
    if (conversationId && firebaseUserId) {
      return FIREBASE.db
        .collection(CollectionName.Sites)
        .doc(SITE_SLUG)
        .collection(CollectionName.Conversations)
        .doc(conversationId)
        .collection(CollectionName.Messages)
        .orderBy('sent', 'desc')
        .onSnapshot(
          querySnapshot => {
            // subsequent changes. this is where we care about new messages received, etc.
            const docChanges = querySnapshot.docChanges();
            docChanges.forEach(docChange => {
              if (docChange.type === 'added') {
                const messageSnapshot = docChange.doc;
                const messageData = messageSnapshot.data() as Message;

                let intentAction: string;
                let intentDisplayName: string;
                let intentName: string;
                let intentDetectionConfidence: number;

                if (
                  messageData.receivedBy &&
                  messageData.receivedBy.indexOf(firebaseUserId) === -1
                ) {
                  /** fire Google Analytics event for all new messages received */
                  try {
                    if (messageData.type === MessageType.BotAgent) {
                      intentName = (messageData as BotAgentMessage)
                        .dialogflowQueryResult?.intent?.name;
                      intentDisplayName = (messageData as BotAgentMessage)
                        .dialogflowQueryResult?.intent?.displayName;
                      intentDetectionConfidence = (messageData as BotAgentMessage)
                        .dialogflowQueryResult?.intentDetectionConfidence;
                      intentAction = (messageData as BotAgentMessage)
                        .dialogflowQueryResult?.action;

                      trackGA4Event(
                        GA4Event.MessageReceivedAutomated,
                        {
                          message: messageData.text,
                          intent_action: intentAction,
                          intent_display_name: intentDisplayName,
                          intent_name: intentName,
                          intent_detection_confidence: intentDetectionConfidence,
                          chat_window_open: isOpen,
                        },
                        true
                      );
                    }

                    /** Every User message or Live Agent message get an event */
                    if (messageData.type === MessageType.User) {
                      const { userType } = messageData;

                      if (userType === 'customer') {
                        trackGA4Event(
                          GA4Event.MessageSentCustomer,
                          {
                            message: messageData.text,
                          },
                          true
                        );
                      } else if (userType === 'support') {
                        trackGA4Event(
                          GA4Event.MessageReceivedSupport,
                          {
                            message: messageData.text,
                          },
                          true
                        );
                      }
                    }
                  } catch (error) {
                    logError(error, {
                      message: `Failed to send Google Analytics event for message: ${messageData.id}`,
                      level: 'warning',
                    });
                  }

                  // We can now update this message to indicate that this user has received it
                  messageSnapshot.ref
                    .update({
                      receivedBy: firebase.firestore.FieldValue.arrayUnion(
                        firebaseUserId
                      ),
                    })
                    .catch(err => {
                      logError(err, {
                        category: Category.Messages,
                        message: `Failed to update message ${messageData.id}'s receivedBy`,
                      });
                    });
                }
              }
            });

            const freshMessages: Message[] = [];
            querySnapshot.docs.forEach(doc => {
              const data = doc.data() as FirebaseMessage;
              if (data.type === 'userAction') {
                // we don't show user actions (e.g. Page View) inside ChatBot
                return;
              }
              const message = { ...data, sent: data.sent.toDate() } as Message;
              freshMessages.push({ id: doc.id, ...message } as Message);
            });
            setMessages(freshMessages);
          },
          err => {
            if (FIREBASE.auth.currentUser) {
              logError(err, {
                category: Category.Conversations,
                message: `User [${FIREBASE.auth.currentUser.uid}] failed to subscribe to conversation [${conversationId}] Messages snapshot`,
                level: 'fatal',
                type: 'error',
              });
            }
            // do nothing, so that messages still appear in chatbot
            // setMessages([]);
          }
        );
    }
  }, [conversationId, isOpen, firebaseUserId]);

  const supportOrBotMessages = messages.filter(isMessageFromSupportOrBot);
  /** Most recent message from Support or ChatBot */
  const latestIncomingMessage = supportOrBotMessages[0];
  /** Number of messages from Support or ChatBot that haven't been seen */
  const unseenCount = supportOrBotMessages.filter(
    m => !m.seenBy.includes(firebaseUserId)
  ).length;

  return { messages, unseenCount, latestIncomingMessage };
};
